import { useMemo, useRef, useState } from 'react';
import { Button, MenuItem, TableCell, Tooltip } from '@mui/material';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import FormatListNumberedSharpIcon from '@mui/icons-material/FormatListNumbered';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import { useTranslation } from 'react-i18next';
import { CatalogSearchDialog, Show } from '..';
import {
  useAuthentication,
  useFormFieldValue,
  useNotification,
  useQuotableCables,
  useQuoteDetails,
  useQuoteMarkets,
} from '../../hooks';
import { Cable, Quote, QuoteProduct } from '../../models';
import { StyledTableRow } from '../../tables';
import { convertQuoteProductPackageLength } from '../../utils/quote';
import { MeasurementUnits, NotificationLevel } from '../../enums';
import { getDefaultQuoteProduct } from '../../utils/quoteProduct';
import { FileUpload } from '../FileUpload/FileUpload.component';
import { QuoteImportCsv } from '../../imports/csv/quoteImportCsv';
import { QuoteProductsTableCatalogProps } from './QuoteProductsTableCatalog.props';
import { MenuItems } from './Components/MenuItems/MenuItems.component';

export const QuoteProductsTableCatalog = ({
  form,
  units,
  setUnits,
  catalogFilters,
  onCatalogFiltersChange,
  verifyCatalogFilters,
  priceUnits,
  setPriceUnits,
}: QuoteProductsTableCatalogProps) => {
  const { t } = useTranslation();
  const { isReadonly } = useQuoteDetails(form);
  const { isAdmin } = useAuthentication();
  const { list: cables } = useQuotableCables();
  const { addNotification } = useNotification();

  const [catalogSearchDialogOpen, setCatalogSearchDialogOpen] = useState(false);

  const onCatalogSearchOpen = () => {
    verifyCatalogFilters();
    setCatalogSearchDialogOpen(true);
  };

  const { value: products, setValue: setProducts } = useFormFieldValue<
    Quote,
    Array<QuoteProduct>
  >(form, 'products');

  const { canSeeCSA, canSeeUL } = useQuoteMarkets(form);

  const canSeeMarket = useMemo(
    () => Boolean(canSeeCSA || canSeeUL),
    [canSeeCSA, canSeeUL]
  );

  const addProductToQuote = (product: Cable, quantity: number) => {
    if (!products?.find((p) => p.partNumber === product.id)) {
      const newProduct: QuoteProduct = {
        ...product,
        lineItem: (products?.length || 0) + 1,
        minimumOrderQuantity: convertQuoteProductPackageLength(
          product.packageLength || 0,
          units
        ),
        price: 0,
        discount: 0,
        partNumber: product.id,
        quantity,
        customLeadTime: null,
      };

      setProducts([...(products || []), newProduct]);
    }
  };

  const addCustomProductToQuote = () => {
    const customProduct: QuoteProduct = getDefaultQuoteProduct(
      true,
      products?.length
    );

    setProducts([...(products || []), customProduct]);
  };

  const addPartNumberToQuote = () => {
    const customProduct: QuoteProduct = getDefaultQuoteProduct(
      false,
      products?.length
    );

    setProducts([...(products || []), customProduct]);
  };

  const updateProductFromQuote = (product: Cable, quantity: number) => {
    const updatedProducts =
      products?.map((p) =>
        p.partNumber === product.id ? { ...p, quantity } : p
      ) || [];

    setProducts(updatedProducts);
  };

  const toggleUnitPriceUnits = () => {
    setPriceUnits?.(
      priceUnits === MeasurementUnits.Imperial
        ? MeasurementUnits.Metric
        : MeasurementUnits.Imperial
    );
  };

  const handleFileUpload = (file: File) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const fileData = event.target?.result as string;
            importProductsFromCSV(fileData);
          } catch {
            addNotification(
              t('fileUpload.parsingError'),
              NotificationLevel.Error
            );
          }
        };
        reader.readAsText(file);
      } catch {
        addNotification(t('fileUpload.error'), NotificationLevel.Error);
      }
    }
  };

  const importProductsFromCSV = (data: string) => {
    const { products: newProducts, errors } = QuoteImportCsv.import(
      data,
      t,
      cables,
      units,
      form.item
    );

    if (errors.length > 0) {
      throw new Error(errors[0].message);
    }

    setProducts([...(products || []), ...newProducts]);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Show if={canSeeMarket && !isReadonly}>
        <StyledTableRow
          sx={{ borderTop: { xs: 'none' } }}
          id="custom-first-row"
        >
          <TableCell sx={{ p: 0.5 }} colSpan={isAdmin ? 5 : 12}>
            <MenuItems label={t('quotes.edit.products.table.actions.add')}>
              <MenuItem onClick={onCatalogSearchOpen} disableRipple>
                <CreateNewFolderOutlinedIcon />
                {t('quotes.edit.products.table.actions.addFromCatalog')}
              </MenuItem>
              {isAdmin && (
                <MenuItem onClick={addCustomProductToQuote} disableRipple>
                  <AddBoxOutlinedIcon />
                  {t('quotes.edit.products.table.actions.addCustomProduct')}
                </MenuItem>
              )}
              <MenuItem onClick={addPartNumberToQuote} disableRipple>
                <FormatListNumberedSharpIcon />
                {t('quotes.edit.products.table.actions.addByPartNumber')}
              </MenuItem>
              {isAdmin && (
                <Tooltip
                  title={
                    products?.length > 0
                      ? t('quotes.edit.products.table.tooltips.csvImport')
                      : ''
                  }
                >
                  <span>
                    <MenuItem
                      onClick={() => inputRef.current?.click()}
                      disabled={products?.length > 0}
                      disableRipple
                    >
                      <MoveToInboxOutlinedIcon />
                      {t('quotes.edit.products.table.actions.csvImport')}
                    </MenuItem>
                  </span>
                </Tooltip>
              )}
            </MenuItems>
            <FileUpload
              inputRef={inputRef}
              handleFileUpload={handleFileUpload}
              acceptedFileTypes={'.csv'}
            />
          </TableCell>
          {isAdmin && (
            <TableCell sx={{ p: 0.5 }} colSpan={7}>
              <Button
                sx={{ pl: 2 }}
                startIcon={<RefreshIcon />}
                onClick={toggleUnitPriceUnits}
              >
                {t('quotes.edit.products.table.actions.toggleUnits')}
              </Button>
            </TableCell>
          )}
        </StyledTableRow>
      </Show>
      {canSeeMarket && (
        <CatalogSearchDialog
          units={units}
          setUnits={setUnits}
          form={form}
          onAddProduct={addProductToQuote}
          onUpdateProduct={updateProductFromQuote}
          catalogSearchDialogOpen={catalogSearchDialogOpen}
          setCatalogSearchDialogOpen={setCatalogSearchDialogOpen}
          catalogFilters={catalogFilters}
          onCatalogFiltersChange={onCatalogFiltersChange}
        />
      )}
    </>
  );
};
